<template>
    <div class="cz3__gallery"
        :class="{
            'cz3__gallery--with-padding': addPadding,
        }"
    >
        <div v-if="noHeader !== true" class="cz3__gallery__header">
            {{$t('V3_CHOOSE_IMAGE')}}
        </div>

        <div v-for="group in availableGalleryImages" :key="group.category" class="cz3__gallery__category">
            <div class="cz3__gallery__category-header">{{group.category}}</div>

            <gallery-list-group
                :selectedGalleryImage="selectedGalleryImage"
                :group="group.images"
                :max-items="maxItems"
                @selectGalleryImage="selectGalleryImage"
            />
        </div>

        <transition name="cz2__fade">
            <div v-if="uploadWorking" class="cz3__gallery-busy" ref="spinner-container">
                <div ref="spinner" class="cz3__gallery-busy__spinner">
                    <!-- eslint-disable -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <!-- !Font Awesome Pro 6.6.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc. -->
                        <path d="M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z"/>
                    </svg>
                    <!-- eslint-enable -->
                </div>
            </div>
        </transition>
    </div>
</template>

<style lang="scss">
    @import "../../style/variables.scss";

    #cz2.cz2 {
        .cz3__gallery {
            position: relative;

            width: 100%;

            display: flex;
            flex-direction: column;

            gap: 24px;
        }

        @keyframes cz3__spin-forever {
            0% {
                transform: rotate(0deg);
            }

            100% {
                transform: rotate(360deg);
            }
        }

        .cz3__gallery-busy {
            position: absolute;

            left: 0;
            top: 0;

            width: 100%;
            height: 100%;

            background: rgba(255, 255, 255, 0.8);

            svg {
                width: 40px;
                height: 40px;

                animation: cz3__spin-forever 2s linear infinite;
            }
        }

        .cz3__gallery-busy__spinner {
            position: absolute;

            left: 50%;
            top: 200px;

            transform: translate(-50%, -50%);
        }

        .cz3__gallery__header {
            font-family: $font-main;
            font-weight: 700;
            font-size: 18px;
        }

        .cz3__gallery__category-header {
            margin-bottom: 16px;

            font-family: $font-main;
            font-weight: 500;
            font-size: 14px;
        }

        .cz3__gallery__images {
            display: flex;

            flex-direction: row;
            flex-wrap: wrap;
            gap: 12px;
        }

        .cz3__gallery__image {
            flex: 0 0 auto;

            position: relative;

            width: 73px;
            height: 73px;

            border-radius: 8px;

            border: none;
            background: #f5f5f5;

            &:focus {
                outline: 2px solid #000;
                outline-offset: 2px;
            }

            &.cz3__gallery__image--selected {
                &:after {
                    content: '';

                    position: absolute;

                    left: -4px;
                    top: -4px;
                    right: -4px;
                    bottom: -4px;

                    border-radius: 8px;

                    pointer-events: none;

                    border: 2px solid #000;
                }
            }
        }

        .cz3__gallery__image__image {
            position: absolute;

            left: 8px;
            top: 8px;

            width: calc(100% - 16px);
            height: calc(100% - 16px);

            border-radius: 8px;

            overflow: hidden;

            background-repeat: no-repeat;
            background-size: contain;
            background-position: center;
        }
    }
</style>

<script>
    import Vue from 'vue';
    import axios from 'axios';

    import GalleryListGroup from './GalleryListGroup.vue';

    import * as constants from '../../constants';

    import designArea from '../../utilities/designArea';

    export default {
        components: {
            GalleryListGroup,
        },

        props: {
            noHeader: Boolean,
            addPadding: Boolean,
            maxItems: Number,
        },

        data() {
            return {
                selectedGalleryImage: null,
                uploadWorking: false,
            };
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            /**
             * Currently selected view.
             */
            currentView() {
                return this.$store.state.customizer.currentView;
            },

            personalizationMethod() {
                return this.$store.state.customizer.personalizationMethod;
            },

            availableGalleryImages() {
                if (this.viewController == null) {
                    return [];
                }

                const images = [];

                // Find stock images placement.
                const targetPlacement = this.viewController.findPlacement('ImageGallery');

                if (targetPlacement == null || targetPlacement.components == null) {
                    return images;
                }

                const mappedCategories = this.mapCategoryToName(targetPlacement.components);

                // Scan through components.
                targetPlacement.components.forEach((c) => {
                    // Skip covers.
                    if (c.custom && c.custom.cover === 'true') {
                        return;
                    }

                    const category = mappedCategories[c.custom.category];

                    // TODO: Update Logomark gallery.
                    const canColor = false; // c.custom && c.custom['v-colorable'] === 'true';
                    const canEtch = true; // c.custom && c.custom['v-supports-etching'] === 'true';
                    const canPrint = false; // c.custom && c.custom['v-supports-printing'] === 'true';

                    const name = this.viewController.componentNames(c).join(', ');

                    if (this.personalizationMethod === 'etching' && !canEtch) {
                        return;
                    }

                    if (this.personalizationMethod === 'printing' && !canPrint) {
                        return;
                    }

                    let list = images.find((i) => i.category === category);

                    if (list == null) {
                        list = {
                            category,
                            images: [],
                        };

                        images.push(list);
                    }

                    const url = c.custom.image;

                    const flags = [];

                    if (canEtch) {
                        flags.push('E');
                    }

                    if (canPrint) {
                        flags.push('P');
                    }

                    if (!canColor) {
                        flags.push('N');
                    }

                    list.images.push({
                        url,
                        category,
                        name,

                        flags: flags.join(''),
                    });
                });

                return images;
            },

            /**
             * Gets a list of free images.
             */
            emptyImagePlacements() {
                return this.$store.getters.emptyImagePlacements;
            },

            filledPlacements() {
                return this.$store.getters.filledPlacements;
            },

            /**
             * Figure out a list of available colors.
             */
            availableColors() {
                return this.$store.getters.availablePersonalizationColors;
            },

            /**
             * Get a list of primary colors to be shown in the plain list.
             */
            primaryColors() {
                const primaries = [];

                if (this.lastSelectedNonprimary) {
                    primaries.push(this.lastSelectedNonprimary);
                }

                primaries.push(...this.availableColors.filter((c) => {
                    if (c.custom && c.custom['v-primary-color'] === 'true') {
                        return true;
                    }

                    return false;
                }));

                return primaries;
            },

            editPlacements() {
                return this.$store.state.customizer.editPlacements;
            },

            editPlacement() {
                if (this.editPlacements && this.editPlacements.length > 0) {
                    return this.editPlacements[0];
                }

                return null;
            },
        },

        watch: {
            uploadWorking() {
                this.$emit('uploading', this.uploadWorking);

                if (this.uploadWorking) {
                    this.$nextTick(() => {
                        this.positionSpinner();
                    });
                }
            },
        },

        mounted() {
            // eslint-disable-next-line prefer-destructuring
            this.previewPlacement = this.emptyImagePlacements[0];

            if (this.editPlacement != null) {
                this.previewPlacement = this.viewController.state.find((x) => x.placement.code === this.editPlacement.code);
            }

            this.$nextTick(() => {
                this.positionSpinner();
            });
        },

        methods: {
            /**
             * Select gallery image.
             */
            selectGalleryImage(image) {
                this.selectedGalleryImage = image;

                if (image) {
                    this.uploadGalleryImageInternal(true);
                }
            },

            uploadGalleryImageInternal(previewOnly) {
                if (this.selectedGalleryImage && this.previewPlacement) {
                    let { url } = this.selectedGalleryImage;
                    const { flags } = this.selectedGalleryImage;

                    if (url.startsWith('//')) {
                        url = `https:${url}`;
                    }

                    this.handleFileImage(url, {
                        type: 'gallery',
                        category: this.selectedGalleryImage.category,
                        name: this.selectedGalleryImage.name,
                        previewOnly,
                        targetPlacement: this.previewPlacement,
                        flags,
                    }).then((component) => {
                        Vue.set(component.custom, 'gallery-flags', flags);
                    });
                }
            },

            handleFileImageContinue(file, options, resolve) {
                // Validate the file type.

                if (file instanceof File) {
                    if (!this.mimeTypesSelected.includes(file.type) || this.isBlockedExtension(file.name)) {
                        if (this.personalizationMethod === constants.METHOD_PRINTING) {
                            this.$store.dispatch('showGenericNotify', {
                                title: this.$t('IMAGE_UPLOAD_BAD_TYPE_PRINTING'),
                                description: this.$t('IMAGE_UPLOAD_BAD_TYPE_PRINTING_COPY'),
                                action: this.$t('ACTION_CONTINUE'),
                            });
                        } else {
                            this.$store.dispatch('showGenericNotify', {
                                title: this.$t('IMAGE_UPLOAD_BAD_TYPE_ETCHING'),
                                description: this.$t('IMAGE_UPLOAD_BAD_TYPE_ETCHING_COPY'),
                                action: this.$t('ACTION_CONTINUE'),
                            });
                        }

                        return;
                    }

                    if (this.personalizationMethod === 'printing') {
                        if (file.type.includes('jpeg')) {
                            this.$store.dispatch('showGenericNotify', {
                                title: this.$t('IMAGE_UPLOAD_JPEG'),
                                description: this.$t('IMAGE_UPLOAD_JPEG_COPY'),
                                action: this.$t('ACTION_CONTINUE'),
                            });
                        }
                    }
                }

                const formData = new FormData();

                if (file instanceof File) {
                    formData.append('file', file);
                } else {
                    formData.append('copy-from', file);
                }

                const target = options.targetPlacement.component.custom;

                const imageMinHeight = target && target['dynamic-image-upload-limit-min-height'] ? +target['dynamic-image-upload-limit-min-height'] : null;
                const imageMaxHeight = target && target['dynamic-image-upload-limit-max-height'] ? +target['dynamic-image-upload-limit-max-height'] : null;

                const imageMinWidth = target && target['dynamic-image-upload-limit-min-width'] ? +target['dynamic-image-upload-limit-min-width'] : null;
                const imageMaxWidth = target && target['dynamic-image-upload-limit-max-width'] ? +target['dynamic-image-upload-limit-max-width'] : null;

                if (imageMinHeight && imageMinWidth) {
                    formData.append('min-width', imageMinWidth);
                    formData.append('min-height', imageMinHeight);
                }

                if (imageMaxHeight && imageMaxWidth) {
                    formData.append('max-width', imageMaxWidth);
                    formData.append('max-height', imageMaxHeight);
                }

                const fit = target && target['dynamic-image-upload-fit'] ? +target['dynamic-image-upload-fit'] : null;
                const fitWidth = target && target['dynamic-image-upload-fit-width'] ? +target['dynamic-image-upload-fit-width'] : null;
                const fitHeight = target && target['dynamic-image-upload-fit-height'] ? +target['dynamic-image-upload-fit-height'] : null;

                if (fit && fitWidth && fitHeight) {
                    formData.append('fit', fit);
                    formData.append('fit-width', fitWidth);
                    formData.append('fit-height', fitHeight);
                }

                formData.append('min-raster-width', constants.MIN_RASTER_WIDTH);
                formData.append('min-raster-height', constants.MIN_RASTER_HEIGHT);

                if (this.personalizationMethod === 'etching') {
                    let trace = true;

                    if (typeof file === 'string' && file.includes('.svg')) {
                        trace = false;
                    }

                    if (trace) {
                        formData.append('trace', 'true');
                        formData.append('trace-threshold', '0.5');
                        formData.append('bgc', 'ffffff');
                        //     'min-raster-width': 2000,
                        //     'min-raster-height': 2000,
                        //     'jpeg-preview-size': 500,
                        //     'jpeg-preview-background': '255,255,255,255',
                        // });
                    }
                }

                if (options && options.type === 'gallery') {
                    formData.append('allow-reuse', 'true');
                }

                this.uploadWorking = true;
                this.uploadProgress = 0;

                axios({
                    method: 'post',
                    url: '//api.images.drivecommerce.com/api/v1/image/logomark/upload/',
                    data: formData,
                    config: {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    },
                    onUploadProgress: (progressEvent) => {
                        this.uploadProgress = (progressEvent.loaded / progressEvent.total) * 100;
                    },
                }).then((response) => {
                    this.uploadWorking = false;

                    resolve(this.handleImageResponse(response, file, options));
                }).catch((error) => {
                    this.uploadWorking = false;

                    if (error && error.response && error.response.data) {
                        const { data } = error.response;

                        if (data.modelState && data.modelState[''] && data.modelState[''].length > 0) {
                            if (data.details && data.details.error === 'maximum-dimensions') {
                                this.$store.dispatch('showGenericNotify', {
                                    title: this.$t('IMAGE_UPLOAD_BAD_SIZE'),
                                    description: this.$t('IMAGE_UPLOAD_BAD_SIZE_COPY'),
                                    action: this.$t('ACTION_CONTINUE'),
                                });
                            } else if (data.details && data.details.error === 'font-outlines') {
                                this.$store.dispatch('showGenericNotify', {
                                    title: this.$t('IMAGE_UPLOAD_BAD_OUTLINES'),
                                    description: this.$t('IMAGE_UPLOAD_BAD_OUTLINES_COPY'),
                                    action: this.$t('ACTION_CONTINUE'),
                                });
                            } else {
                                this.$store.dispatch('toast', {
                                    message: data.modelState[''][0],
                                    type: 'error',
                                });
                            }
                        }
                    }
                });
            },

            /**
             * Handles image response.
             */
            handleImageResponse(response, file, {
                type,
                targetPlacement,
                name,
                category,
                flags,
            }) {
                this.$store.dispatch('validateEditableViews');
                this.$store.dispatch('switchToCommonView');

                const image = response.data;

                // Pick a placement to put image to.
                const { placement, component } = targetPlacement;

                this.$store.dispatch('takeEmptyPlacement', placement);

                const { view } = this.currentView;

                const designOn = view.custom['v-design-area'];

                const {
                    left, right, top, bottom,
                } = designArea(this.$store.state.customizer, view.custom['v-design-area']);

                if (left != null && right != null && top != null && bottom != null) {
                    // Figure out initial scaling to fit into the design area.
                    const areaWidth = right - left;
                    const areaHeight = bottom - top;

                    this.viewController.commitCustomAttributes(placement.code, {
                        'dynamic-image-area-width': areaWidth.toString(),
                        'dynamic-image-area-height': areaHeight.toString(),
                    });
                }

                // Configure the image.
                this.viewController.updateDynamicImage(placement.code, {
                    title: image.title,
                    name: image.name,
                    url: image.imageUrl,
                    pngUrl: image.pngImageUrl,
                    jpgUrl: image.jpgImageUrl,
                    width: image.width,
                    height: image.height,
                });

                this.$store.dispatch('renderOnlyOnView', {
                    view: this.currentView,
                });

                const group = this.viewController.blueprint.custom[`v-group-${view.custom['v-design-area']}`];

                this.$store.dispatch('setGroup', { placement: placement.code, group });

                // Update dynamic image will center the image; zero out coordinates.
                this.viewController.commitCustomAttributes(placement.code, {
                    'v-design-on': view.custom['v-design-area'],
                    mask: view.custom['v-design-mask'],
                    'dynamic-image-x': 0,
                    'dynamic-image-y': 0,
                    'effect-colorize': null,
                    'effect-colorize-add': null,
                    'effect-colorize-name': null,
                });

                // Is it colorizable?
                let allowColors = true;

                if ((flags || '').includes('N')) {
                    allowColors = false;
                }

                this.viewController.commitCustomAttributes(placement.code, {
                    'allow-colors': allowColors ? 'true' : 'false',
                });

                // If colorizable, pick the first primary color.
                if (type === 'gallery') {
                    if (allowColors && this.personalizationMethod !== 'etching') {
                        let useColor = null;

                        if (this.primaryColors.length > 0) {
                            // eslint-disable-next-line
                            useColor = this.primaryColors[0];
                        }

                        if (useColor == null && this.availableColors.length > 0) {
                            // eslint-disable-next-line
                            useColor = this.availableColors[0];
                        }

                        this.viewController.commitCustomAttributes(placement.code, {
                            'effect-colorize': '000000',
                            'effect-colorize-add': useColor.custom.hex,
                            'effect-colorize-name': useColor.name,
                        });
                    }
                }

                // ... and center manually in the current view.
                if (view.custom) {
                    if (left != null && right != null && top != null && bottom != null) {
                        // Figure out initial scaling to fit into the design area.
                        const areaWidth = right - left;
                        const areaHeight = bottom - top;

                        const imageWidth = +component.custom['dynamic-image-width'];
                        const imageHeight = +component.custom['dynamic-image-height'];

                        const fitScaleWidth = areaWidth / imageWidth;
                        const fitScaleHeight = areaHeight / imageHeight;

                        const fitScale = Math.min(fitScaleWidth, fitScaleHeight);

                        let yOffset = null;
                        let yOffsetMin = null;

                        this.filledPlacements.forEach((p) => {
                            if (this.personalizationMethod === 'printing') {
                                return;
                            }

                            if (p.placement.code === placement.code) {
                                return;
                            }

                            if (p.component.custom['v-design-on'] !== designOn) {
                                return;
                            }

                            if (p.component.custom['personalization-y'] != null) {
                                if (p.placement.code.startsWith(constants.PLACEMENT_MONOGRAM)) {
                                    const below = +p.component.custom['personalization-y'] + (+p.component.custom['personalization-height']) * 1.5;

                                    if (yOffset == null) {
                                        yOffset = below;
                                    } else {
                                        yOffset = Math.max(below, yOffset);
                                    }

                                    const above = +p.component.custom['personalization-y'] - (+p.component.custom['personalization-height']) * 0.5;

                                    if (yOffsetMin == null) {
                                        yOffsetMin = above;
                                    } else {
                                        yOffsetMin = Math.min(above, yOffsetMin);
                                    }
                                } else {
                                    const below = +p.component.custom['personalization-y'] + (+p.component.custom['personalization-height']) * 1.5;

                                    if (yOffset == null) {
                                        yOffset = below;
                                    } else {
                                        yOffset = Math.max(below, yOffset);
                                    }

                                    const above = +p.component.custom['personalization-y'] - (+p.component.custom['personalization-height']) * 0.5;

                                    if (yOffsetMin == null) {
                                        yOffsetMin = above;
                                    } else {
                                        yOffsetMin = Math.min(above, yOffsetMin);
                                    }
                                }
                            }

                            if (p.component.custom['dynamic-image-height'] != null) {
                                const y = +p.component.custom['dynamic-image-offset-y'];
                                const h = +p.component.custom['dynamic-image-height'];
                                const s = +p.component.custom['dynamic-image-scale'];
                                const totalHeight = +p.component.custom['dynamic-image-area-height'];

                                const below = y + (h * s * 0.5) + (totalHeight * 0.1);

                                if (yOffset == null) {
                                    yOffset = below;
                                } else {
                                    yOffset = Math.max(below, yOffset);
                                }

                                const above = y - (h * s * 0.5) - (totalHeight * 0.1);

                                if (yOffsetMin == null) {
                                    yOffsetMin = above;
                                } else {
                                    yOffsetMin = Math.min(above, yOffsetMin);
                                }
                            }
                        });

                        if (yOffset == null) {
                            if ((this.viewController.blueprint.custom['v-default-alignment-v3'] || '').toLowerCase() === 'top') {
                                yOffset = (imageHeight * fitScale * 0.5) + 10;
                            } else {
                                yOffset = ((top + bottom) * 0.5);
                            }
                        } else {
                            // eslint-disable-next-line no-lonely-if
                            if (yOffset + imageHeight * fitScale > bottom) {
                                if (yOffsetMin < top + (imageHeight * fitScale) * 0.25) {
                                    yOffset = ((top + bottom) * 0.5);
                                } else {
                                    yOffset = yOffsetMin - (imageHeight * fitScale * 0.5);
                                }
                            } else {
                                yOffset += imageHeight * fitScale * 0.5;
                            }
                        }

                        this.viewController.commitCustomAttributes(placement.code, {
                            'dynamic-image-offset-x': ((left + right) * 0.5),
                            'dynamic-image-offset-y': yOffset,
                            'dynamic-image-scale': fitScale.toString(),
                        });
                    }
                }

                if (type === 'gallery') {
                    Vue.set(component.custom, 'gallery-image', 'true');
                    if (name) {
                        Vue.set(component.custom, 'gallery-image-name', name);
                    } else {
                        Vue.set(component.custom, 'gallery-image-name', null);
                    }

                    if (category) {
                        Vue.set(component.custom, 'gallery-image-category', category);
                    } else {
                        Vue.set(component.custom, 'gallery-image-category', null);
                    }
                } else {
                    Vue.set(component.custom, 'gallery-image', 'false');
                }

                if (file instanceof File) {
                    Vue.set(component.custom, 'dynamic-image-original-name', file.name);
                }

                this.beginEdit(placement, component);

                return component;
            },

            beginEdit(placement) {
                this.$store.dispatch('editPlacement', placement);
            },

            /**
             * Handle image upload.
             */
            handleFileImage(file, options) {
                return new Promise((resolve) => {
                    if (file instanceof File) {
                        this.handleFileImageContinue(file, options, resolve);
                    } else {
                        const sniffUrl = `${file}.svg`;

                        axios.head(sniffUrl).then(() => {
                            this.handleFileImageContinue(sniffUrl, options, resolve);
                        }).catch(() => {
                            this.handleFileImageContinue(file, options, resolve);
                        });
                    }
                });
            },

            isBlockedExtension(name) {
                const dot = name.lastIndexOf('.');

                if (dot >= 0) {
                    const ext = name.substr(dot).toLowerCase();

                    if (this.blockExtensions.includes(ext)) {
                        return true;
                    }
                }

                return false;
            },

            findClippingParent(element) {
                let currentElement = element;

                while (currentElement) {
                    const style = window.getComputedStyle(currentElement);

                    if (style.overflow === 'auto') {
                        return currentElement;
                    }

                    currentElement = currentElement.parentElement;
                }

                return null;
            },

            getClippingBoundaryScreenPosition(container) {
                const clippingParent = this.findClippingParent(container);

                if (clippingParent) {
                    const parentRect = clippingParent.getBoundingClientRect();

                    return parentRect.top;
                }

                const ownRect = clippingParent.getBoundingClientRect();

                return ownRect.top;
            },

            positionSpinner() {
                const container = this.$refs['spinner-container'];
                // eslint-disable-next-line prefer-destructuring
                const spinner = this.$refs.spinner;

                // eslint-disable-next-line no-empty
                if (container && spinner) {
                    const windowHeight = window.innerHeight;

                    const containerTop = this.getClippingBoundaryScreenPosition(container);

                    const availableHeight = windowHeight - containerTop;

                    const clippedBy = this.findClippingParent(container);

                    // ... minus buttons.
                    const y = clippedBy.scrollTop + (availableHeight * 0.5) - 80;

                    spinner.style.top = `${y}px`;
                }
            },

            /**
             * Maps category IDs (custom.category) to category titles
             */
            mapCategoryToName(components) {
                const categoryIdToName = {};

                components.forEach((component) => {
                    if (component.custom?.cover === 'true') {
                        const categoryId = component.custom.category;

                        categoryIdToName[categoryId] = component.description[0].name;
                    }
                });

                return categoryIdToName;
            },
        },
    };
</script>
